import React from 'react';
import { useHistory } from 'react-router-dom';
import { Row, Col, Card } from 'react-bootstrap';
import { useMantenedorPermissions } from './hooks/useMantenedorPermissions';
import { getMantenedoresList } from './utils/mantenedorConstants';
import SimpleMantenedorLayout from './components/SimpleMantenedorLayout';
import * as FaIcons from 'react-icons/fa';

const Mantenedores = () => {
  const history = useHistory();
  const { 
    filterMantenedoresByPermission,
    permissionsLoading,
    permissionsError 
  } = useMantenedorPermissions();
  
  // Obtener la lista de mantenedores desde la configuración centralizada
  const allMantenedores = getMantenedoresList();
  
  // Filtrar mantenedores según permisos del usuario
  const mantenedoresList = filterMantenedoresByPermission(allMantenedores);

  const handleMantenedorClick = (mantenedor) => {
    // console.log(`Navegando a ${mantenedor.titulo}`, mantenedor);
    history.push(mantenedor.path);
  };

  // Estados de carga y error se manejan automáticamente en SimpleMantenedorLayout
  if (permissionsLoading) {
    return (
      <SimpleMantenedorLayout
        mantenedorId="mantenedores"
        title="Sistema General Cierre de Conductores"
        subtitle="Cargando permisos..."
        icon={<FaIcons.FaSpinner className="fa-spin" />}
        iconColor="#667eea"
        showBackButton={false}
      >
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '200px',
          textAlign: 'center' 
        }}>
          <div>
            <FaIcons.FaSpinner 
              className="fa-spin" 
              style={{ 
                fontSize: '2rem', 
                color: '#667eea', 
                marginBottom: '16px' 
              }} 
            />
            <p style={{ color: '#6c757d', margin: '0' }}>
              Verificando permisos de acceso...
            </p>
          </div>
        </div>
      </SimpleMantenedorLayout>
    );
  }

  if (permissionsError) {
    return (
      <SimpleMantenedorLayout
        mantenedorId="mantenedores"
        title="Sistema General Cierre de Conductores"
        subtitle="Error al cargar permisos"
        icon={<FaIcons.FaExclamationTriangle />}
        iconColor="#dc3545"
        showBackButton={false}
      >
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '200px',
          textAlign: 'center' 
        }}>
          <div style={{
            padding: '40px',
            backgroundColor: '#ffebee',
            borderRadius: '8px',
            border: '1px solid #f48fb1',
            maxWidth: '500px'
          }}>
            <FaIcons.FaExclamationTriangle 
              style={{ 
                fontSize: '2rem', 
                color: '#c62828', 
                marginBottom: '16px' 
              }} 
            />
            <h3 style={{ color: '#c62828', marginBottom: '12px' }}>
              Error al cargar permisos
            </h3>
            <p style={{ color: '#666', margin: '0' }}>
              Ha ocurrido un error al verificar tus permisos. Intenta recargar la página.
            </p>
          </div>
        </div>
      </SimpleMantenedorLayout>
    );
  }

  return (
    <SimpleMantenedorLayout
      mantenedorId="mantenedores"
      title="Sistema General Cierre de Conductores"
      subtitle="Gestiona, administra los diferentes mantenedores y revisa los cierres de conductores"
      icon={<FaIcons.FaCog />}
      iconColor="#667eea"
      showBackButton={false}
    >
      {mantenedoresList.length === 0 ? (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '200px',
          textAlign: 'center' 
        }}>
          <div>
            <FaIcons.FaLock 
              style={{ 
                fontSize: '2rem', 
                color: '#6c757d', 
                marginBottom: '16px' 
              }} 
            />
            <h3 style={{ color: '#495057', marginBottom: '8px' }}>
              Sin acceso a mantenedores
            </h3>
            <p style={{ color: '#6c757d', margin: '0' }}>
              No tienes permisos para acceder a ningún mantenedor en este momento.
            </p>
          </div>
        </div>
      ) : (
        <Row>
          {mantenedoresList.map((mantenedor) => (
            <Col xs={12} sm={6} md={4} lg={3} key={mantenedor.id} className="mb-4">
              <Card 
                className="h-100"
                onClick={() => handleMantenedorClick(mantenedor)}
                style={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  border: '1px solid #dee2e6',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                }}
              >
                <Card.Body className="d-flex flex-column align-items-center text-center p-4">
                  <div 
                    className="mb-3"
                    style={{ 
                      background: `linear-gradient(135deg, ${mantenedor.color} 0%, ${mantenedor.color}dd 100%)`,
                      width: '50px',
                      height: '50px',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      color: 'white'
                    }}
                  >
                    {mantenedor.icon}
                  </div>
                  <Card.Title 
                    as="h6" 
                    className="mb-2 text-truncate w-100" 
                    style={{ 
                      fontSize: '1rem', 
                      fontWeight: '600',
                      color: '#212529'
                    }}
                  >
                    {mantenedor.titulo}
                  </Card.Title>
                  <Card.Text 
                    className="text-muted small text-center"
                    style={{ 
                      fontSize: '0.85rem', 
                      lineHeight: '1.3',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      color: '#6c757d'
                    }}
                  >
                    {mantenedor.descripcion}
                  </Card.Text>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </SimpleMantenedorLayout>
  );
};

export default Mantenedores;